import { motion } from 'framer-motion'
import { Target, Heart, Zap, Users, Award, Rocket, Globe, Phone, Mail, MapPin, QrCode, CheckCircle } from 'lucide-react'

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="section-title">关于我们</h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              专注人工智能大模型技术，赋能千行百业数字化转型
            </p>
          </motion.div>

          {/* 公司简介 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="tech-card p-8 md:p-12">
              <h2 className="text-3xl font-bold text-white mb-6">公司简介</h2>
              <p className="text-white/70 mb-4 leading-relaxed text-lg">
                大连音智科技有限公司是一家专注于 AI 语音识别与大数据技术的国家高新技术企业，致力于为教育、农业、医疗、人社、应急等行业提供智能化解决方案。
              </p>
              <p className="text-white/70 mb-4 leading-relaxed text-lg">
                公司以"用 AI 技术让世界更安全、更高效"为使命，通过自主研发的"静守™"校园安全管控系统和"KK"政企数字化转型产品，已服务全国 100+ 客户，获得 50+ 技术专利认可。
              </p>
              <p className="text-white/70 leading-relaxed text-lg">
                我们拥有一支由海内外知名高校博士、硕士组成的专业研发团队，在语音识别、自然语言处理、大数据分析等领域拥有深厚的技术积累，多项技术指标达到国际领先水平。
              </p>
            </div>
          </motion.div>

          {/* 我们的价值观 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <h2 className="section-title text-center mb-12">我们的价值观</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                {
                  letter: 'S',
                  word: 'Service',
                  chinese: '服务至上',
                  icon: Heart,
                  color: 'from-blue-500 to-cyan-500',
                  descLine1: '重视微小需求',
                  descLine2: '事事细致落实',
                },
                {
                  letter: 'U',
                  word: 'Unity',
                  chinese: '团结协作',
                  icon: Users,
                  color: 'from-green-500 to-teal-500',
                  descLine1: '响应紧急诉求',
                  descLine2: '行动快速敏捷',
                },
                {
                  letter: 'P',
                  word: 'Professional',
                  chinese: '专业精进',
                  icon: Award,
                  color: 'from-purple-500 to-pink-500',
                  descLine1: '对标完美标准',
                  descLine2: '打磨极致品质',
                },
                {
                  letter: 'E',
                  word: 'Excellence',
                  chinese: '追求卓越',
                  icon: Target,
                  color: 'from-orange-500 to-red-500',
                  descLine1: '优化成本效率',
                  descLine2: '实现价值最优',
                },
                {
                  letter: 'R',
                  word: 'Responsibility',
                  chinese: '责任担当',
                  icon: CheckCircle,
                  color: 'from-yellow-500 to-orange-500',
                  descLine1: '深度共情客户',
                  descLine2: '达成双向共鸣',
                },
              ].map((value, index) => (
                <motion.div
                  key={value.letter}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="tech-card text-center p-6 group"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg group-hover:shadow-tech-500/30 transition-all`}>
                    <span className="text-3xl font-bold text-white">{value.letter}</span>
                  </div>
                  <value.icon className="w-8 h-8 text-tech-400 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-white mb-1">{value.word}</h3>
                  <p className="text-tech-400 font-semibold mb-2">{value.chinese}</p>
                  <p className="text-white/60 text-sm leading-relaxed">
                    <span>{value.descLine1}</span>
                    <br />
                    <span>{value.descLine2}</span>
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* 核心优势 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <h2 className="section-title text-center mb-12">核心优势</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: Zap,
                  title: '技术领先',
                  desc: '50+ 技术专利，50+ 专业研发团队',
                  color: 'from-yellow-500 to-orange-500',
                },
                {
                  icon: Award,
                  title: '行业深耕',
                  desc: '服务 100+ 客户，覆盖教育、农业、医疗等多个行业',
                  color: 'from-blue-500 to-cyan-500',
                },
                {
                  icon: Rocket,
                  title: '产品创新',
                  desc: '静守™校园安全管控系统、KK 政企数字化转型产品',
                  color: 'from-purple-500 to-pink-500',
                },
                {
                  icon: Globe,
                  title: '生态合作',
                  desc: '与阿里云、华为云等建立战略合作关系',
                  color: 'from-green-500 to-teal-500',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  className="tech-card text-center p-8"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-white/60">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* 数据亮点 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <div className="tech-card p-8 md:p-12">
              <h2 className="section-title text-center mb-12">数据亮点</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { number: '50+', label: '技术专利', icon: Award },
                  { number: '50+', label: '研发团队', icon: Users },
                  { number: '100+', label: '服务客户', icon: Globe },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2, duration: 0.6 }}
                    whileHover={{ scale: 1.05 }}
                    className="text-center p-6"
                  >
                    <stat.icon className="w-16 h-16 text-tech-400 mx-auto mb-4" />
                    <div className="text-6xl font-bold gradient-text mb-3">{stat.number}</div>
                    <div className="text-white/60 text-xl">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* 联系方式 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-title text-center mb-12">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="tech-card p-8">
                <h3 className="text-2xl font-bold text-white mb-6">联系方式</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-tech-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">公司地址</h4>
                      <p className="text-white/70">辽宁省大连市品海街 57 号音智科技</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-tech-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">联系电话</h4>
                      <p className="text-white/70">18500089321</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-tech-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">商务邮箱</h4>
                      <p className="text-white/70">sales@aiyinzhi.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <QrCode className="w-6 h-6 text-tech-400 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">微信公众号</h4>
                      <p className="text-white/70">音智</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="tech-card p-8 flex flex-col items-center justify-center">
                <h3 className="text-2xl font-bold text-white mb-6">关注公众号</h3>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <img 
                    src="https://down.dingtalk.com/media/lQDPM4xFT3ei0FnNAVjNAViwbMqq2XAO98cJkWkvxJ98AA_344_344.jpg" 
                    alt="音智科技微信公众号" 
                    className="w-[180px] h-[180px] object-contain"
                  />
                </div>
                <p className="text-white/60 text-center">扫描二维码关注"音智"公众号</p>
                <p className="text-white/40 text-sm mt-2">获取最新资讯与产品信息</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage
